import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Input } from '@nokowazzi/ui';
import { adminMeQuery } from '../features/admin/queries';
import type { AdminMeResponse } from '../features/admin/queries';
import { ApiError, apiFetch } from '../lib/api';
import { queryClient } from '../lib/query-client';
import { supabase } from '../lib/supabase';

interface SigninSearch {
  /** 로그인 성공 후 돌아갈 경로 */
  redirect?: string;
  /** 가드가 붙여주는 리다이렉트 사유 */
  reason?: 'forbidden';
}

export const Route = createFileRoute('/signin')({
  validateSearch: (search: Record<string, unknown>): SigninSearch => ({
    redirect: typeof search['redirect'] === 'string' ? search['redirect'] : undefined,
    reason: search['reason'] === 'forbidden' ? 'forbidden' : undefined,
  }),
  component: SignInScreen,
});

function SignInScreen() {
  const router = useRouter();
  const search = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(
    search.reason === 'forbidden' ? '관리자 권한이 없는 계정입니다' : null,
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError('이메일 또는 비밀번호가 올바르지 않습니다');
        return;
      }

      // 로그인 성공 → 어드민 여부 확인 (403이면 권한 없음 + 로그아웃)
      try {
        const me = await apiFetch<AdminMeResponse>('/v1/admin/me');
        queryClient.setQueryData(adminMeQuery().queryKey, me);
      } catch (meError) {
        if (meError instanceof ApiError && (meError.status === 401 || meError.status === 403)) {
          await supabase.auth.signOut();
          queryClient.clear();
          setError('관리자 권한이 없는 계정입니다');
          return;
        }
        throw meError;
      }

      router.history.push(search.redirect ?? '/');
    } catch {
      setError('로그인 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-page px-6">
      <div className="w-full max-w-sm rounded-lg border border-line bg-surface p-8 shadow-card">
        <h1 className="text-title2 text-fg">노코와찌 어드민</h1>
        <p className="mt-1 text-caption1 text-fg-secondary">관리자 계정으로 로그인하세요</p>
        <form className="mt-6 flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <Input
            label="이메일"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="비밀번호"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error != null && (
            <p role="alert" className="text-caption1 text-danger-600">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            로그인
          </Button>
        </form>
      </div>
    </main>
  );
}
